import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../components/Header";
import api from "../../services/api";
import { showSuccess, showError } from "../../services/alert";
import LocationPicker from "../../components/LocationPicker";
import "./TalhaoForm.css";

const TalhaoForm = () => {
  const { id } = useParams();
  const isEditing = Boolean(id && id !== "create");
  const navigate = useNavigate();

  const [propriedades, setPropriedades] = useState([]);
  const [form, setForm] = useState({
    propriedadeId: "",
    nome: "",
    areaHa: "",
    latitude: "",
    longitude: "",
    ativo: true
  });
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    api.get("/propriedades")
      .then(({ data }) => setPropriedades(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEditing) return;

    setLoading(true);
    api.get(`/talhoes/${id}`)
      .then(({ data }) => setForm({
        propriedadeId: data.propriedadeId,
        nome: data.nome,
        areaHa: data.areaHa,
        latitude: data.latitude ?? "",
        longitude: data.longitude ?? "",
        ativo: data.ativo,
      }))
      .catch(() => showError("Erro ao carregar registro."))
      .finally(() => setLoading(false));
  }, [id, isEditing]);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleToggle = (e) => setForm(prev => ({ ...prev, ativo: e.target.checked }));
  const handleLocation = (latitude, longitude) => setForm(prev => ({ ...prev, latitude, longitude }));
  const handlePropriedade = async (e) => {
    const propriedadeId = e.target.value;
    const propriedade = propriedades.find(item => String(item.id) === String(propriedadeId));
    setForm(prev => ({
      ...prev,
      propriedadeId,
      latitude: propriedade?.latitude ?? "",
      longitude: propriedade?.longitude ?? "",
    }));
    if (propriedade && propriedade.latitude == null && propriedade.longitude == null && propriedade.cidade) {
      setLocating(true);
      try {
        const response = await api.get("/geocodificacao", { params: { cidade: propriedade.cidade, estado: propriedade.estado || "" } });
        const data = response.data;
        if (data?.latitude != null && data?.longitude != null) handleLocation(Number(Number(data.latitude).toFixed(6)), Number(Number(data.longitude).toFixed(6)));
      } catch { /* O mapa permanece centralizado no Brasil para seleção manual. */ }
      finally { setLocating(false); }
    }
  };

  const propriedadeSelecionada = propriedades.find(item => String(item.id) === String(form.propriedadeId));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.propriedadeId) { showError("Selecione uma Propriedade."); return; }
    if (!form.nome.trim()) { showError('O campo "Nome" é obrigatório.'); return; }
    if (form.areaHa === "" || Number(form.areaHa) < 0) { showError("Informe a área (ha) válida."); return; }
    if (form.latitude !== "" && (Number(form.latitude) < -90 || Number(form.latitude) > 90)) { showError("Latitude deve ficar entre -90 e 90."); return; }
    if (form.longitude !== "" && (Number(form.longitude) < -180 || Number(form.longitude) > 180)) { showError("Longitude deve ficar entre -180 e 180."); return; }

    const payload = {
      id: isEditing ? Number(id) : 0,
      propriedadeId: Number(form.propriedadeId),
      nome: form.nome.trim(),
      areaHa: Number(form.areaHa),
      latitude: form.latitude === "" ? null : Number(form.latitude),
      longitude: form.longitude === "" ? null : Number(form.longitude),
      ativo: form.ativo
    };

    setLoading(true);
    try {
      if (isEditing) {
        await api.put(`/talhoes/${id}`, payload);
        showSuccess("Talhão atualizado com sucesso.");
      } else {
        await api.post("/talhoes", payload);
        showSuccess("Talhão criado com sucesso.");
      }
      navigate("/talhoes");
    } catch (err) {
      showError(err?.response?.data?.message || "Erro ao salvar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <Header />
      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex align-items-center gap-3 rounded-top-3">
          <button
            className="btn btn-light btn-sm d-flex align-items-center gap-1"
            onClick={() => navigate("/talhoes")}
            disabled={loading}
          >
            <i className="bi bi-arrow-left text-success" />
            <span className="text-success fw-semibold">Voltar</span>
          </button>
          <h5 className="mb-0">
            {isEditing ? "Editar talhão" : "Novo talhão"}
          </h5>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-4">
            <fieldset className="border p-3 rounded-3">
              <legend className="px-3 text-success fw-semibold">Dados do talhão</legend>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Propriedade *</label>
                  <select
                    name="propriedadeId"
                    value={form.propriedadeId}
                    onChange={handlePropriedade}
                    className="form-select rounded-3"
                    required
                    disabled={loading || locating}
                  >
                    <option value="">Selecione...</option>
                    {(propriedades || []).map(p => (
                      <option key={p.id} value={p.id}>
                        {p.nome}{p.agricultorNome ? ` - ${p.agricultorNome}` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Nome *</label>
                  <input
                    type="text"
                    name="nome"
                    value={form.nome}
                    onChange={handleChange}
                    className="form-control rounded-3"
                    maxLength={160}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Área (ha) *</label>
                  <input
                    type="number"
                    name="areaHa"
                    step="0.01"
                    min="0"
                    value={form.areaHa}
                    onChange={handleChange}
                    className="form-control rounded-3"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="col-12 talhao-location">
                  <div className="talhao-location-heading">
                    <div>
                      <i className="bi bi-geo-alt-fill" />
                      <span>
                        <strong>Onde fica o talhão?</strong>
                        <small>{locating ? "Localizando a propriedade..." : propriedadeSelecionada?.cidade ? `${propriedadeSelecionada.cidade}${propriedadeSelecionada.estado ? ` - ${propriedadeSelecionada.estado}` : ""}` : "Selecione uma propriedade e marque a área no mapa."}</small>
                      </span>
                    </div>
                    {form.latitude !== "" && form.longitude !== "" && <span className="talhao-location-confirmed"><i className="bi bi-check-circle-fill" /> Localização marcada</span>}
                  </div>
                  {form.propriedadeId ? <>
                    <LocationPicker latitude={form.latitude} longitude={form.longitude} onChange={handleLocation} disabled={loading} locationLabel="Usar localização do dispositivo" />
                    <small className="talhao-location-help"><i className="bi bi-info-circle" /> Use a localização do celular/computador ou toque no mapa para marcar o centro do talhão.</small>
                  </> : <div className="talhao-location-empty"><i className="bi bi-map" /><strong>Selecione a propriedade</strong><span>O mapa será aberto automaticamente na região da fazenda.</span></div>}
                </div>

                {isEditing && (
                  <div className="col-md-6 d-flex align-items-end">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={form.ativo}
                        onChange={handleToggle}
                        disabled={loading}
                      />
                      <label className="form-check-label ms-2">Ativo</label>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-12 d-flex justify-content-end mt-3">
                <button type="submit" className="btn btn-success me-2 d-flex align-items-center gap-1" disabled={loading}>
                  <i className="bi bi-save" />
                  <span>{loading ? "Salvando..." : "Salvar"}</span>
                </button>
                <button type="button" className="btn btn-secondary d-flex align-items-center gap-1" onClick={() => navigate("/talhoes")} disabled={loading}>
                  <i className="bi bi-x-circle" />
                  <span>Cancelar</span>
                </button>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TalhaoForm;
